/**
 * Panning over the poles on the globe.
 *
 * Cam, 2026-09-07: "I don't like how when you pan across the globe you get hard locked at north and
 * south, just let it keep going."
 *
 * WHY IT LOCKS. MapLibre's globe pan ends with, in `computeGlobePanCenter`:
 *
 *     clamp(lat + rotatedPanDelta.y * degPerPx, -MAX_VALID_LATITUDE, MAX_VALID_LATITUDE)
 *
 * with MAX_VALID_LATITUDE = 85.051129 — the Mercator limit, because the tile pyramid genuinely ends
 * there. And it is not only the drag: the transform clamps too, so `jumpTo({center: [0, 89]})` on a
 * globe silently lands at 85.0511 (measured 2026-09-07). There is no map above that latitude to pan
 * onto.
 *
 * SO DO NOT FIGHT THE CLAMP — CROSS THE POLE INSTEAD. Going over the top of a sphere and continuing
 * down the far side is the same as stepping to the opposite meridian and reversing heading:
 *
 *     lon += 180,  bearing += 180
 *
 * Latitude never leaves the legal range, no internal MapLibre maths is reimplemented, and the drag
 * carries straight on: rotating the bearing by π negates the rotated pan delta, so the same finger
 * motion that was climbing now descends the other side. The globe keeps turning under the cursor,
 * which is what "let it keep going" means.
 *
 * ⚠️ The tradeoff, stated because it is visible: the cap between 85.05° and the pole is skipped, so
 * crossing jumps ~9.9° of latitude. That cap has no tiles in any Mercator basemap, ours included,
 * so it is a region with no map rather than a region we decline to show. At globe zoom it is a few
 * pixels of ocean; at street zoom nobody is at the pole.
 */

/** MapLibre's own MAX_VALID_LATITUDE — the Mercator limit, and where the tiles stop. */
export const POLE_LIMIT = 85.051129;
/** How close to the limit counts as pinned against it. */
const EPS = 1e-4;

export interface PoleCross { lon: number; bearing: number }

/**
 * The crossing to apply, or null if the pan is not running into a pole.
 *
 * `rotatedY` is the pan delta already rotated into map space — the same quantity MapLibre adds to
 * the latitude, so positive means "heading north".
 *
 * Pure so the topology can be tested without a map: the whole feature is four lines of angle
 * arithmetic and every bug in it would be a sign error.
 */
export function poleCross(lat: number, lon: number, bearing: number, rotatedY: number): PoleCross | null {
  const atNorth = lat >= POLE_LIMIT - EPS && rotatedY > 0;
  const atSouth = lat <= -POLE_LIMIT + EPS && rotatedY < 0;
  if (!atNorth && !atSouth) return null;
  return { lon: wrapLon(lon + 180), bearing: wrapBearing(bearing + 180) };
}

/** Longitude into (-180, 180]. */
export function wrapLon(lon: number): number {
  const x = ((lon + 180) % 360 + 360) % 360 - 180;
  return x === -180 ? 180 : x;
}

/** Bearing into [0, 360). */
export function wrapBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * The pan delta rotated into map space, matching MapLibre's `Point.rotate(bearingInRadians)`:
 * x' = x·cos − y·sin, y' = x·sin + y·cos. Only y is needed.
 */
export function rotatedPanY(panX: number, panY: number, bearingDeg: number): number {
  const b = (bearingDeg * Math.PI) / 180;
  return panX * Math.sin(b) + panY * Math.cos(b);
}
